'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import QuestMap from './QuestMap';
import ActiveQuestsList from './ActiveQuestsList';
import QuestSubmissionPortal from './QuestSubmissionPortal';
import QuestArchive from './QuestArchive';
import CommunityGallery from './CommunityGallery';

interface QuestLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
  questCount: number;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Legendary';
  status: 'Active' | 'Completed' | 'Locked';
}

interface Quest {
  id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Legendary';
  timeEstimate: string;
  participants: number;
  maxParticipants: number;
  progress: number;
  deadline: string;
  rewards: string[];
  category: string;
  image: string;
  alt: string;
  location: string;
  status: 'Active' | 'Full' | 'Ending Soon';
}

interface ArchivedQuest {
  id: string;
  title: string;
  description: string;
  completedDate: string;
  participants: number;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Legendary';
  category: string;
  image: string;
  alt: string;
  rewards: string[];
  successRate: number;
  communityRating: number;
  highlights: string[];
}

interface GallerySubmission {
  id: string;
  title: string;
  author: string;
  authorAvatar: string;
  authorAvatarAlt: string;
  questTitle: string;
  submissionType: 'Photo' | 'Video' | 'Research' | 'Creative';
  image: string;
  alt: string;
  likes: number;
  comments: number;
  submittedDate: string;
  featured: boolean;
  tags: string[];
}

const LoadingSkeleton = () =>
<div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:74:0" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="74" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
    <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:75:4" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="75" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%20p-6%22%7D" className="animate-pulse space-y-6 p-6">
      <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:76:6" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="76" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-8 bg-muted rounded w-1/3"></div>
      <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:77:6" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="77" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:78:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="78" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-4%22%7D" className="lg:col-span-2 space-y-4">
          <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:79:10" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="79" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg"></div>
          <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:80:10" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="80" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
            {[1, 2, 3].map((i) =>
          <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:82:10" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="82" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-24%20bg-muted%20rounded-lg%22%7D" key={i} className="h-24 bg-muted rounded-lg"></div>
          )}
          </div>
        </div>
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:86:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="86" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg"></div>
      </div>
    </div>
  </div>;


const QuestCommandContent = () => {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedQuest, setSelectedQuest] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubmissionType, setSelectedSubmissionType] = useState('All');

  // Mock data
  const questLocations: QuestLocation[] = [
  {
    id: 'san-francisco',
    name: 'San Francisco, CA',
    lat: 37.7749,
    lng: -122.4194,
    questCount: 8,
    difficulty: 'Medium',
    status: 'Active'
  },
  {
    id: 'london',
    name: 'London, UK',
    lat: 51.5074,
    lng: -0.1278,
    questCount: 12,
    difficulty: 'Hard',
    status: 'Active'
  },
  {
    id: 'tokyo',
    name: 'Tokyo, Japan',
    lat: 35.6762,
    lng: 139.6503,
    questCount: 6,
    difficulty: 'Easy',
    status: 'Active'
  },
  {
    id: 'copenhagen',
    name: 'Copenhagen, Denmark',
    lat: 55.6761,
    lng: 12.5683,
    questCount: 15,
    difficulty: 'Legendary',
    status: 'Active'
  },
  {
    id: 'melbourne',
    name: 'Melbourne, Australia',
    lat: -37.8136,
    lng: 144.9631,
    questCount: 4,
    difficulty: 'Medium',
    status: 'Completed'
  },
  {
    id: 'montreal',
    name: 'Montreal, Canada',
    lat: 45.5017,
    lng: -73.5673,
    questCount: 9,
    difficulty: 'Hard',
    status: 'Active'
  }];


  const activeQuests: Quest[] = [
  {
    id: 'metallica-venue-hunt',
    title: 'Historic Venue Documentation',
    description: 'Document and photograph historic venues where Metallica performed during their early years. Research the significance and capture the current state of these legendary locations.',
    difficulty: 'Medium',
    timeEstimate: '3-5 hours',
    participants: 127,
    maxParticipants: 200,
    progress: 68,
    deadline: '2025-01-15T23:59:59Z',
    rewards: ['Exclusive venue photos', 'Historical timeline access', '50 community points'],
    category: 'Research',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15e16b4f8-1763436192896.png",
    alt: 'Historic concert venue with vintage architecture and stage lighting',
    location: 'San Francisco, CA',
    status: 'Active'
  },
  {
    id: 'master-of-puppets-tribute',
    title: 'Master of Puppets 40th Anniversary',
    description: 'Create tribute content celebrating the 40th anniversary of Master of Puppets. Submit covers, artwork, or written pieces honoring this masterpiece album.',
    difficulty: 'Hard',
    timeEstimate: '5-8 hours',
    participants: 89,
    maxParticipants: 150,
    progress: 45,
    deadline: '2025-02-01T23:59:59Z',
    rewards: ['Anniversary badge', 'Rare album artwork', '100 community points'],
    category: 'Creative',
    image: "https://images.unsplash.com/photo-1725365176341-ff853382ddc1",
    alt: 'Vintage recording studio with analog equipment and guitar amplifiers',
    location: 'London, UK',
    status: 'Active'
  },
  {
    id: 'metallica-memorabilia-hunt',
    title: 'Rare Memorabilia Discovery',
    description: 'Hunt for and document rare Metallica memorabilia in local record stores, vintage shops, and personal collections. Share photos and stories behind each find.',
    difficulty: 'Easy',
    timeEstimate: '2-4 hours',
    participants: 156,
    maxParticipants: 300,
    progress: 82,
    deadline: '2025-01-20T23:59:59Z',
    rewards: ['Collector badge', 'Memorabilia guide', '25 community points'],
    category: 'Collection',
    image: "https://images.unsplash.com/photo-1712044962053-53c322cb878b",
    alt: 'Vintage record store with vinyl albums and band memorabilia on display',
    location: 'Tokyo, Japan',
    status: 'Ending Soon'
  },
  {
    id: 'lars-drumkit-recreation',
    title: "Lars Ulrich's Drum Kit Recreation",
    description: 'Recreate Lars Ulrich\'s iconic drum setups from different eras. Document the process and perform classic Metallica drum parts using the recreated kits.',
    difficulty: 'Legendary',
    timeEstimate: '10+ hours',
    participants: 23,
    maxParticipants: 50,
    progress: 15,
    deadline: '2025-03-01T23:59:59Z',
    rewards: ['Legendary drummer badge', 'Exclusive drum samples', '200 community points'],
    category: 'Performance',
    image: "https://images.unsplash.com/photo-1707409189962-0e6cdf81fb16",
    alt: 'Professional drum kit setup with multiple cymbals and percussion instruments',
    location: 'Copenhagen, Denmark',
    status: 'Active'
  },
  {
    id: 'metallica-cover-challenge',
    title: 'Metallica Cover Challenge',
    description: 'Record high-quality covers of classic Metallica songs. Focus on technical accuracy and creative interpretation while maintaining the essence of the originals.',
    difficulty: 'Hard',
    timeEstimate: '6-10 hours',
    participants: 78,
    maxParticipants: 120,
    progress: 33,
    deadline: '2025-01-25T23:59:59Z',
    rewards: ['Performer badge', 'Cover compilation feature', '75 community points'],
    category: 'Performance',
    image: "https://images.unsplash.com/photo-1601856254555-a9c0ebef8af3",
    alt: 'Electric guitar and amplifier setup in professional recording studio',
    location: 'Montreal, Canada',
    status: 'Active'
  }];


  const archivedQuests: ArchivedQuest[] = [
  {
    id: 'black-album-30th',
    title: 'Black Album 30th Anniversary Celebration',
    description: 'Community-wide celebration of The Black Album\'s 30th anniversary with covers, artwork, and historical documentation.',
    completedDate: '2024-08-12T00:00:00Z',
    participants: 342,
    difficulty: 'Medium',
    category: 'Creative',
    image: "https://images.unsplash.com/photo-1720981403828-d62cd3371cf2",
    alt: 'Black and white photo of vintage recording equipment and vinyl records',
    rewards: ['Anniversary badge', 'Exclusive artwork collection', 'Historical timeline'],
    successRate: 89,
    communityRating: 4.7,
    highlights: [
    'Over 200 unique cover submissions',
    'Rare behind-the-scenes footage unlocked',
    'Community collaboration album created']

  },
  {
    id: 'metallica-world-tour',
    title: 'World Tour Documentation Project',
    description: 'Global effort to document Metallica\'s world tour stops with photos, interviews, and local fan stories.',
    completedDate: '2024-06-20T00:00:00Z',
    participants: 567,
    difficulty: 'Hard',
    category: 'Research',
    image: "https://images.unsplash.com/photo-1496042297528-d36738bca593",
    alt: 'Large concert venue filled with enthusiastic fans and stage lighting',
    rewards: ['Global explorer badge', 'Tour documentary access', 'Exclusive interviews'],
    successRate: 94,
    communityRating: 4.9,
    highlights: [
    'Documented 45 tour stops worldwide',
    'Collected 1,200+ fan stories',
    'Created interactive tour map']

  },
  {
    id: 'garage-days-revisited',
    title: 'Garage Days Revisited Challenge',
    description: 'Recreation of Metallica\'s garage days with raw, unpolished covers and original compositions in the same spirit.',
    completedDate: '2024-04-15T00:00:00Z',
    participants: 189,
    difficulty: 'Easy',
    category: 'Performance',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14af30c7d-1763436193010.png",
    alt: 'Garage band setup with basic equipment and raw acoustic atmosphere',
    rewards: ['Garage rocker badge', 'Raw recordings collection', 'DIY guide'],
    successRate: 76,
    communityRating: 4.3,
    highlights: [
    'Over 150 garage-style recordings',
    'Authentic vintage equipment used',
    'Community jam sessions organized']

  }];


  const gallerySubmissions: GallerySubmission[] = [
  {
    id: 'venue-photo-1',
    title: 'The Fillmore West - Where It All Began',
    author: 'MetalHistorian',
    authorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11f4a03f8-1763294742899.png",
    authorAvatarAlt: 'Profile photo of middle-aged man with beard wearing black metal band t-shirt',
    questTitle: 'Historic Venue Documentation',
    submissionType: 'Photo',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17c7fcba2-1763436192458.png",
    alt: 'Historic Fillmore West venue exterior with vintage marquee and classic architecture',
    likes: 234,
    comments: 45,
    submittedDate: '2024-11-15T10:30:00Z',
    featured: true,
    tags: ['venue', 'history', 'fillmore', 'classic']
  },
  {
    id: 'drum-recreation-1',
    title: 'Lars 1986 Kit Recreation - Justice Era',
    author: 'DrumMaster88',
    authorAvatar: "https://images.unsplash.com/photo-1686590513290-6cbe332a5ff9",
    authorAvatarAlt: 'Young drummer with long hair holding drumsticks in recording studio',
    questTitle: "Lars Ulrich's Drum Kit Recreation",
    submissionType: 'Video',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4e9b602-1763436194022.png",
    alt: 'Detailed recreation of Lars Ulrich 1986 drum kit with vintage cymbals and hardware',
    likes: 189,
    comments: 67,
    submittedDate: '2024-11-14T16:45:00Z',
    featured: true,
    tags: ['drums', 'lars', 'justice', 'recreation']
  },
  {
    id: 'memorabilia-find-1',
    title: 'Original Kill Em All Vinyl - Mint Condition',
    author: 'VinylCollector',
    authorAvatar: "https://images.unsplash.com/photo-1624022889501-cd2830f38cfb",
    authorAvatarAlt: 'Woman with glasses examining vintage vinyl record in record store',
    questTitle: 'Rare Memorabilia Discovery',
    submissionType: 'Photo',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec41b44e-1763436192483.png",
    alt: 'Pristine original Kill Em All vinyl record with intact shrink wrap and promotional sticker',
    likes: 156,
    comments: 23,
    submittedDate: '2024-11-13T14:20:00Z',
    featured: false,
    tags: ['vinyl', 'killEmAll', 'rare', 'mint']
  },
  {
    id: 'cover-performance-1',
    title: 'Master of Puppets - Full Band Cover',
    author: 'MetalBandX',
    authorAvatar: "https://images.unsplash.com/photo-1541002741547-d972eae576e6",
    authorAvatarAlt: 'Band members in black clothing posing with instruments in rehearsal space',
    questTitle: 'Metallica Cover Challenge',
    submissionType: 'Video',
    image: "https://images.unsplash.com/photo-1599587243082-6c1c8d91d363",
    alt: 'Four-piece metal band performing on stage with professional lighting and equipment',
    likes: 298,
    comments: 89,
    submittedDate: '2024-11-12T20:15:00Z',
    featured: true,
    tags: ['cover', 'masterOfPuppets', 'band', 'performance']
  },
  {
    id: 'artwork-tribute-1',
    title: 'Digital Art - Ride the Lightning Reimagined',
    author: 'DigitalArtist',
    authorAvatar: "https://images.unsplash.com/photo-1661546394067-51fcb4af5145",
    authorAvatarAlt: 'Young female artist with colorful hair working on digital tablet in art studio',
    questTitle: 'Master of Puppets 40th Anniversary',
    submissionType: 'Creative',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16f49851b-1763436192892.png",
    alt: 'Modern digital artwork reimagining Ride the Lightning album cover with neon colors',
    likes: 167,
    comments: 34,
    submittedDate: '2024-11-11T11:30:00Z',
    featured: false,
    tags: ['artwork', 'digital', 'ridetheLightning', 'tribute']
  },
  {
    id: 'research-doc-1',
    title: 'The Evolution of Metallica\'s Sound - Academic Analysis',
    author: 'MusicScholar',
    authorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_196226ae4-1763436192011.png",
    authorAvatarAlt: 'Academic researcher with glasses reading music theory books in library',
    questTitle: 'Historic Venue Documentation',
    submissionType: 'Research',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_142569b26-1763436191124.png",
    alt: 'Academic research document with musical notation and sound wave analysis charts',
    likes: 89,
    comments: 56,
    submittedDate: '2024-11-10T09:45:00Z',
    featured: false,
    tags: ['research', 'analysis', 'evolution', 'academic']
  }];


  const categories = ['All', 'Research', 'Creative', 'Performance', 'Collection'];
  const submissionTypes = ['All', 'Photo', 'Video', 'Research', 'Creative'];

  const tabs = [
  { id: 'map', name: 'Quest Map', icon: 'MapIcon' },
  { id: 'active', name: 'Active Quests', icon: 'PlayIcon' },
  { id: 'submit', name: 'Submit', icon: 'PaperAirplaneIcon' },
  { id: 'archive', name: 'Archive', icon: 'ArchiveBoxIcon' },
  { id: 'gallery', name: 'Gallery', icon: 'PhotoIcon' }];


  useEffect(() => {
    setIsHydrated(true);

    // Check for URL parameters
    const tab = searchParams.get('tab');
    const quest = searchParams.get('quest');
    const location = searchParams.get('location');

    if (tab && tabs.some((t) => t.id === tab)) {
      setActiveTab(tab);
    }
    if (quest) {
      setSelectedQuest(quest);
    }
    if (location) {
      setSelectedLocation(location);
    }
  }, [searchParams]);

  const handleLocationSelect = (locationId: string) => {
    setSelectedLocation(selectedLocation === locationId ? null : locationId);
  };

  const handleQuestSelect = (questId: string) => {
    setSelectedQuest(questId);
    setActiveTab('submit');
  };

  const handleSubmissionComplete = (questId: string, submission: any) => {
    // Handle submission completion
    console.log('Submission completed:', { questId, submission });
    setActiveTab('gallery');
  };

  if (!isHydrated) {
    return <LoadingSkeleton data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:452:11" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="452" data-component-file="QuestCommandInteractive.tsx" data-component-name="LoadingSkeleton" data-component-content="%7B%22elementName%22%3A%22LoadingSkeleton%22%7D" />;
  }

  return (
    <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:456:4" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="456" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      {/* Header */}
      <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:458:6" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="458" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border-b%20border-border%22%7D" className="bg-card border-b border-border">
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:459:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="459" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20py-8%22%7D" className="max-w-7xl mx-auto px-6 py-8">
          <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:460:10" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="460" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-6%22%7D" className="flex items-center justify-between mb-6">
            <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:461:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="461" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h1 data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:462:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="462" data-component-file="QuestCommandInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Global%20Quests%20Command%20Center%22%7D" className="text-3xl font-bold text-text-primary mb-2">Global Quests Command Center</h1>
              <p data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:463:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="463" data-component-file="QuestCommandInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Coordinate%20with%20the%20community%20to%20unlock%20exclusive%20Metallica%20content%20through%20collaborative%20quests%22%7D" className="text-text-secondary">
                Coordinate with the community to unlock exclusive Metallica content through collaborative quests
              </p>
            </div>
            <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:467:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="467" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%22%7D" className="flex items-center space-x-4">
              <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:468:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="468" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2%20bg-muted%20rounded-lg%22%7D" className="flex items-center space-x-2 px-4 py-2 bg-muted rounded-lg">
                <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:469:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="469" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-2%20h-2%20bg-success%20rounded-full%20animate-pulse-subtle%22%7D" className="w-2 h-2 bg-success rounded-full animate-pulse-subtle"></div>
                <span data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:470:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="470" data-component-file="QuestCommandInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-mono%20text-text-secondary%22%2C%22textContent%22%3A%22active%20participants%22%7D" className="text-sm font-mono text-text-secondary">
                  {activeQuests.reduce((sum, quest) => sum + quest.participants, 0)} active participants
                </span>
              </div>
              <button data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:474:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="474" data-component-file="QuestCommandInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2%20bg-accent%20text-accent-foreground%20rounded-lg%20font-semibold%20hover%3Abg-conversion-accent%20transition-colors%20duration-300%22%7D" className="flex items-center space-x-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-conversion-accent transition-colors duration-300">
                <Icon data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:475:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="475" data-component-file="QuestCommandInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PlusIcon%22%7D" name="PlusIcon" size={16} />
                <span data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:476:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="476" data-component-file="QuestCommandInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Create%20Quest%22%7D">Create Quest</span>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:482:10" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="482" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-1%20bg-muted%20p-1%20rounded-lg%22%7D" className="flex items-center space-x-1 bg-muted p-1 rounded-lg">
            {tabs.map((tab) =>
            <button data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:484:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="484" data-component-file="QuestCommandInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-all duration-300 ${
              activeTab === tab.id ?
              'bg-card text-accent shadow-sm' :
              'text-text-secondary hover:text-text-primary hover:bg-card/50'}`
              }>

                <Icon data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:493:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="493" data-component-file="QuestCommandInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={tab.icon as any} size={16} />
                <span data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:494:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="494" data-component-file="QuestCommandInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{tab.name}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:502:6" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="502" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20py-8%22%7D" className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'map' &&
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:504:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="504" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            <QuestMap data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:505:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="505" data-component-file="QuestCommandInteractive.tsx" data-component-name="QuestMap" data-component-content="%7B%22elementName%22%3A%22QuestMap%22%7D"
            locations={questLocations}
            selectedLocation={selectedLocation}
            onLocationSelect={handleLocationSelect} />

            
            {selectedLocation &&
          <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:512:10" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="512" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-6%22%7D" className="bg-card border border-border rounded-lg p-6">
                <h3 data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:513:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="513" data-component-file="QuestCommandInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Quests%20in%22%7D" className="text-lg font-semibold text-text-primary mb-4">
                  Quests in {questLocations.find((l) => l.id === selectedLocation)?.name}
                </h3>
                <ActiveQuestsList data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:516:16" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="516" data-component-file="QuestCommandInteractive.tsx" data-component-name="ActiveQuestsList" data-component-content="%7B%22elementName%22%3A%22ActiveQuestsList%22%7D"
              quests={activeQuests.filter((q) => q.location === questLocations.find((l) => l.id === selectedLocation)?.name)}
              selectedCategory={selectedCategory}
              onQuestSelect={handleQuestSelect} />

              </div>
          }
          </div>
        }

        {activeTab === 'active' &&
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:527:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="527" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {/* Category Filter */}
            <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:529:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="529" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%22%7D" className="flex items-center space-x-4">
              <span data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:530:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="530" data-component-file="QuestCommandInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-text-primary%22%2C%22textContent%22%3A%22Filter%20by%20category%3A%22%7D" className="text-sm font-medium text-text-primary">Filter by category:</span>
              <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:531:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="531" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
                {categories.map((category) =>
              <button data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:533:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="533" data-component-file="QuestCommandInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                selectedCategory === category ?
                'bg-accent text-accent-foreground' :
                'bg-muted text-text-secondary hover:bg-accent/20 hover:text-accent'}`
                }>

                    {category}
                  </button>
              )}
              </div>
            </div>

            <ActiveQuestsList data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:548:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="548" data-component-file="QuestCommandInteractive.tsx" data-component-name="ActiveQuestsList" data-component-content="%7B%22elementName%22%3A%22ActiveQuestsList%22%7D"
            quests={activeQuests}
            selectedCategory={selectedCategory}
            onQuestSelect={handleQuestSelect} />

          </div>
        }

        {activeTab === 'submit' &&
        <QuestSubmissionPortal data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:557:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="557" data-component-file="QuestCommandInteractive.tsx" data-component-name="QuestSubmissionPortal" data-component-content="%7B%22elementName%22%3A%22QuestSubmissionPortal%22%7D"
          selectedQuest={selectedQuest}
          onSubmissionComplete={handleSubmissionComplete} />

        }

        {activeTab === 'archive' &&
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:564:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="564" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {/* Category Filter */}
            <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:566:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="566" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%22%7D" className="flex items-center space-x-4">
              <span data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:567:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="567" data-component-file="QuestCommandInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-text-primary%22%2C%22textContent%22%3A%22Filter%20by%20category%3A%22%7D" className="text-sm font-medium text-text-primary">Filter by category:</span>
              <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:568:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="568" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
                {categories.map((category) =>
              <button data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:570:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="570" data-component-file="QuestCommandInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                selectedCategory === category ?
                'bg-accent text-accent-foreground' :
                'bg-muted text-text-secondary hover:bg-accent/20 hover:text-accent'}`
                }>

                    {category}
                  </button>
              )}
              </div>
            </div>

            <QuestArchive data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:585:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="585" data-component-file="QuestCommandInteractive.tsx" data-component-name="QuestArchive" data-component-content="%7B%22elementName%22%3A%22QuestArchive%22%7D"
            archivedQuests={archivedQuests}
            selectedCategory={selectedCategory} />

          </div>
        }

        {activeTab === 'gallery' &&
        <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:593:8" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="593" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {/* Type Filter */}
            <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:595:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="595" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%22%7D" className="flex items-center space-x-4">
              <span data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:596:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="596" data-component-file="QuestCommandInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-text-primary%22%2C%22textContent%22%3A%22Filter%20by%20type%3A%22%7D" className="text-sm font-medium text-text-primary">Filter by type:</span>
              <div data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:597:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="597" data-component-file="QuestCommandInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
                {submissionTypes.map((type) =>
              <button data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:599:14" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="599" data-component-file="QuestCommandInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={type}
                onClick={() => setSelectedSubmissionType(type)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                selectedSubmissionType === type ?
                'bg-accent text-accent-foreground' :
                'bg-muted text-text-secondary hover:bg-accent/20 hover:text-accent'}`
                }>

                    {type}
                  </button>
              )}
              </div>
            </div>

            <CommunityGallery data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:614:12" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="614" data-component-file="QuestCommandInteractive.tsx" data-component-name="CommunityGallery" data-component-content="%7B%22elementName%22%3A%22CommunityGallery%22%7D"
            submissions={gallerySubmissions}
            selectedType={selectedSubmissionType} />

          </div>
        }
      </div>
    </div>);

};

const QuestCommandInteractive = () => {
  return (
    <Suspense fallback={<LoadingSkeleton data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:627:24" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="627" data-component-file="QuestCommandInteractive.tsx" data-component-name="LoadingSkeleton" data-component-content="%7B%22elementName%22%3A%22LoadingSkeleton%22%7D" />}>
      <QuestCommandContent data-component-id="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx:628:6" data-component-path="src/app/global-quests-command-center/components/QuestCommandInteractive.tsx" data-component-line="628" data-component-file="QuestCommandInteractive.tsx" data-component-name="QuestCommandContent" data-component-content="%7B%22elementName%22%3A%22QuestCommandContent%22%7D" />
    </Suspense>);

};

export default QuestCommandInteractive;